import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import mixpanel from "mixpanel-browser";

import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import {
  establishSessionFromTokens,
  NetmeraLoginFailedEvent,
} from "@/features/auth";
import { useToast } from "@/features/toast";
import { getAppleLocale, loadAppleSdk } from "@/lib/load-apple-sdk";
import { createUuid } from "@/lib/uuid";
import useAuthStore from "@/store/use-auth-store";

interface AppleTokenApiResponse {
  success: boolean;
  message?: string;
  code?: string;
  data?: {
    accessToken: string;
    refreshToken: string;
    isNewUser: boolean;
  };
}

// Kullanıcı popup'ı kapattığında/iptal ettiğinde hata gösterilmez.
const CANCEL_CODES = ["popup_closed_by_user", "user_cancelled_authorize"];

/**
 * Popup modunda Apple, redirectURI'nin sayfanın origin'i ile AYNI olmasını
 * şart koşar; aksi halde opaque bir "invalid_request / Invalid web redirect url"
 * döner. Yanlış konfigürasyonu Apple'a sormadan, anlaşılır şekilde yakalıyoruz.
 */
function resolveRedirectUri(): string {
  const configured = import.meta.env.VITE_APPLE_REDIRECT_URI;

  if (!configured) {
    throw new Error(
      "VITE_APPLE_REDIRECT_URI tanımlı değil. Apple Services ID altında kayıtlı origin olmalı.",
    );
  }

  let configuredOrigin: string;
  try {
    configuredOrigin = new URL(configured).origin;
  } catch {
    throw new Error(
      `VITE_APPLE_REDIRECT_URI geçerli bir URL değil: "${configured}"`,
    );
  }

  if (configuredOrigin !== window.location.origin) {
    throw new Error(
      `Apple redirectURI origin'i sayfa origin'i ile eşleşmiyor. ` +
        `Sayfa: ${window.location.origin} — redirectURI: ${configuredOrigin}. ` +
        `Popup modunda ikisi aynı olmalı; uygulamayı Apple'da kayıtlı https domain üzerinden açın.`,
    );
  }

  return configuredOrigin;
}

/**
 * Apple JS SDK ile popup modunda giriş.
 *
 * Redirect akışının aksine sayfa hiç terk edilmez: popup imzalı bir id_token
 * döner, biz de onu backend'e (POST /auth/apple/token) doğrulatıp oturumu
 * yerinde kurarız.
 */
export function useAppleSignIn(mode: "login" | "register") {
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const { success, error } = useToast();
  const { setUser, setTokens } = useAuthStore();
  const navigate = useNavigate();

  const signIn = useCallback(async () => {
    setIsLoading(true);

    try {
      // SDK'yı indirmeden önce konfigürasyonu doğrula.
      const redirectURI = resolveRedirectUri();

      await loadAppleSdk(getAppleLocale(i18n.language));

      if (!window.AppleID) {
        throw new Error("apple_sdk_load_failed");
      }

      // Popup modunda state CSRF koruması sağlamaz (yanıt doğrudan opener'a
      // postMessage ile döner). Yine de ucuz bir yanıt-eşleştirme kontrolü.
      const state = createUuid();

      window.AppleID.auth.init({
        clientId: import.meta.env.VITE_APPLE_SERVICE_ID,
        scope: "name email",
        redirectURI,
        state,
        usePopup: true,
      });

      const appleResponse = await window.AppleID.auth.signIn();

      if (
        appleResponse.authorization.state &&
        appleResponse.authorization.state !== state
      ) {
        throw new Error("apple_state_mismatch");
      }

      const response = await apiClient.post<AppleTokenApiResponse>(
        API_ENDPOINTS.appleToken,
        {
          id_token: appleResponse.authorization.id_token,
          // Apple sadece ilk yetkilendirmede gönderir; sonraki girişlerde yok.
          user: appleResponse.user,
        },
      );

      if (!response?.data?.accessToken) {
        // Backend mesajı http-exception.filter tarafından x-locale'e göre zaten
        // çevrilmiş geliyor — tekrar t()'den geçirmiyoruz.
        throw new Error(response?.message || t("error.somethingWentWrong"));
      }

      await establishSessionFromTokens({
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken || null,
        isNewUser: response.data.isNewUser,
        provider: "apple",
        setTokens,
        setUser,
        success,
        t,
        navigate,
      });
    } catch (errorCatch: any) {
      // SDK düz bir obje ile reject eder ({ error: "..." }), Error değil.
      const appleErrorCode = errorCatch?.error;
      if (CANCEL_CODES.includes(appleErrorCode)) {
        return;
      }

      const backendMessage = errorCatch?.response?.data?.message;
      const failureReason =
        backendMessage || errorCatch?.message || t("error.somethingWentWrong");

      console.error("Apple sign in error:", errorCatch);
      NetmeraLoginFailedEvent("apple", failureReason);
      mixpanel.track("user_logged_in_failed", {
        login_method: "apple_login",
        failure_reason: failureReason,
      });
      error(failureReason, {
        title: t(mode === "login" ? "auth.loginError" : "auth.registerError"),
      });
    } finally {
      setIsLoading(false);
    }
  }, [i18n.language, t, error, success, setUser, setTokens, navigate, mode]);

  return { signIn, isLoading };
}
